<template>
    <div class="hidden lg:block bg-zinc-900 border-b border-zinc-800">
        <div class="max-w-[1400px] mx-auto px-6 py-12">
            <div class="flex flex-col items-center text-center">
                <h1 class="text-5xl font-bold text-white tracking-tight">
                    {{ heroTitle }}
                </h1>

                <!-- View: All -->
                <template v-if="view === 'all' || view === 'explore'">
                    <p v-if="isUserLoggedIn" class="mt-4 text-xl text-white/70 max-w-2xl leading-relaxed">
                        Every star you give builds your Sponsor profile. Earn badges, climb the leaderboard, and show off the performers you've backed.
                    </p>
                    <p v-else class="mt-4 text-xl text-white/70 max-w-2xl leading-relaxed">
                        Every star you give builds your public Sponsor profile — earn badges, climb the leaderboard, and get credit for the scenes you help create.
                    </p>

                    <SearchInput
                        :searchQuery="searchQuery"
                        @searchInput="$emit('searchInput', $event)"
                        @clearSearch="$emit('clearSearch')"
                        @searchFocus="$emit('searchFocus')"
                    />

                    <div class="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-white/55">
                        <div class="flex items-center gap-2">
                            <div class="w-1.5 h-1.5 rounded-full bg-green-500" />
                            <span>
                                <span class="text-white/90 font-semibold">{{ formatStars(totalStarsGiven) }}</span> stars given
                            </span>
                        </div>

                        <div class="h-4 w-px bg-white/10" />

                        <div class="flex items-center gap-2">
                            <TrendingUp class="w-4 h-4 text-white/45" />
                            <span>
                                <span class="text-white/90 font-semibold">{{ sponsoredLast30Days }}</span> sponsored last 30 days
                            </span>
                        </div>

                        <div class="h-4 w-px bg-white/10" />

                        <button
                            type="button"
                            @click="$emit('myNaClicked')"
                            class="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                        >
                            {{ isUserLoggedIn ? 'View Your Sponsor Page' : 'Create Your Sponsor Page'}}
                            <ExternalLink class="w-4 h-4" />
                        </button>
                    </div>

                    <!-- Top Sponsors - Dedicated Line on Desktop -->
                    <div v-if="topSponsors.length > 0" class="mt-4 flex items-center justify-center gap-3">
                        <div class="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-white/[0.04] border border-white/[0.08]">
                            <Trophy class="w-4.5 h-4.5 text-amber-400 shrink-0" />
                            <div class="flex items-center gap-3 text-sm min-w-0">
                                <button v-for="(sponsor, index) in topSponsors.slice(0, maxVisibleTopSponsors)"
                                     :key="sponsor.user_id"
                                     @click="goToSponsorProfile(sponsor)"
                                     class="flex items-center gap-1.5 flex-shrink-0 hover:opacity-80 transition-opacity">
                                    <span class="w-5 h-5 rounded-full bg-gradient-to-br flex items-center justify-center text-[10px] font-bold text-black" :class="rankBadgeColor(sponsor.rank)">{{ sponsor.rank }}</span>
                                    <span class="text-white font-semibold">{{ sponsor.sponsor_name }}</span>
                                    <span class="font-semibold text-xs" :class="rankTextColor(sponsor.rank)">{{ sponsor.total_stars }}</span>
                                    <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-3.5 h-3.5" />
                                    <div v-if="index < maxVisibleTopSponsors - 1" class="w-px h-4 bg-white/10"></div>
                                </button>
                            </div>
                            <button
                                @click="$emit('openLeaderboard')"
                                class="text-white/25 hover:text-white/50 transition text-xs font-medium ml-1 flex-shrink-0"
                            >
                                View All →
                            </button>
                        </div>
                    </div>
                </template>

                <!-- View: Mine -->
                <template v-else-if="view === 'mine'">
                    <p class="mt-4 text-xl text-white/65 max-w-2xl leading-relaxed">
                        Track what you've sponsored and see which auditions are
                        closest to becoming full scenes.
                    </p>

                    <SearchInput
                        :searchQuery="searchQuery"
                        @searchInput="$emit('searchInput', $event)"
                        @clearSearch="$emit('clearSearch')"
                        @searchFocus="$emit('searchFocus')"
                    />

                    <div class="mt-6 flex flex-wrap items-center gap-6 text-sm text-white/55">
                        <span>
                            You've sponsored
                            <span class="text-white/90 font-semibold">
                              {{ userSponsoredCount }}
                            </span>
                            auditions
                        </span>
                        <div class="h-4 w-px bg-white/10" />
                        <span>
                            Total stars given:
                            <span class="text-white/90 font-semibold">
                                {{ userTotalStarsGiven }}
                            </span>
                        </span>
                    </div>
                </template>

                <!-- View: Sponsored -->
                <template v-else-if="view === 'sponsored'">
                    <p class="mt-4 text-xl text-white/65 max-w-2xl leading-relaxed">
                        Auditions that have received star support from members, working their way toward a full production.
                    </p>

                    <SearchInput
                        :searchQuery="searchQuery"
                        @searchInput="$emit('searchInput', $event)"
                        @clearSearch="$emit('clearSearch')"
                        @searchFocus="$emit('searchFocus')"
                    />

                    <div class="mt-6 flex flex-wrap items-center gap-6 text-sm text-white/55">
                        <span>
                            <span class="text-white/90 font-semibold">{{ totalSponsored }}</span>
                            auditions sponsored
                        </span>
                        <div class="h-4 w-px bg-white/10" />
                        <div class="flex items-center gap-2">
                            <div class="w-1.5 h-1.5 rounded-full bg-green-500" />
                            <span>
                                <span class="text-white/90 font-semibold">{{ formatStars(sponsoredTotalStarsGiven) }}</span> stars given
                            </span>
                        </div>
                    </div>
                </template>

                <!-- View: Released -->
                <template v-else-if="view === 'released'">
                    <p class="mt-4 text-xl text-white/65 max-w-2xl leading-relaxed">
                        Watch the full scenes made possible by member sponsorships.
                    </p>

                    <SearchInput
                        :searchQuery="searchQuery"
                        @searchInput="$emit('searchInput', $event)"
                        @clearSearch="$emit('clearSearch')"
                        @searchFocus="$emit('searchFocus')"
                    />

                    <div class="mt-6 flex flex-wrap items-center gap-6 text-sm text-white/55">
                        <span>
                            <span class="text-white/90 font-semibold">
                                {{ releasedCount }}
                            </span>
                            scenes released
                        </span>
                        <div class="h-4 w-px bg-white/10" />
                        <span>
                            <span class="text-white/90 font-semibold">{{ sponsoredThisMonth }}</span>
                            funded this month
                        </span>
                    </div>
                </template>

                <!-- View: Castings -->
                <template v-else-if="view === 'castings'">
                    <p class="mt-4 text-xl text-white/65 max-w-2xl leading-relaxed">
                        Fan-requested scenes waiting to happen. Support a pitch and help bring it to life.
                    </p>

                    <div class="mt-6 flex flex-wrap items-center gap-6 text-sm text-white/55">
                        <Link
                            :href="getCreateCastingUrl(isUserLoggedIn, routePrefix)"
                            @click="handleCreateCastingClick"
                            class="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                        >
                            + Submit a Pitch Request
                        </Link>

                        <div class="h-4 w-px bg-white/10" />

                        <button
                            type="button"
                            @click="handleCastingsPageClick"
                            class="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                        >
                            Your Pitches
                            <ExternalLink class="w-4 h-4" />
                        </button>
                    </div>
                </template>
            </div>
        </div>
    </div>

    <div ref="heroRef">
        <MobileHero
            :view="view"
            :isUserLoggedIn="isUserLoggedIn || false"
            :sponsoredThisMonth="sponsoredThisMonth"
            :sponsoredLast30Days="sponsoredLast30Days"
            :totalSponsored="totalSponsored"
            :sponsoredTotalStarsGiven="sponsoredTotalStarsGiven"
            :totalStarsGiven="totalStarsGiven"
            :userSponsoredCount="userSponsoredCount"
            :releasedCount="releasedCount"
            :userTotalStarsGiven="userTotalStarsGiven"
            :topSponsors="topSponsors"
            :routePrefix="routePrefix"
            @joinClick="$emit('joinClick')"
            @openLeaderboard="$emit('openLeaderboard')"
            @myNaClicked="$emit('myNaClicked')"
            @castingsPageClick="handleCastingsPageClick"
        />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import {Link, router} from '@inertiajs/vue3'
import { TrendingUp, Trophy, ExternalLink } from 'lucide-vue-next'
import MobileHero from "@/Components/Reels/MobileHero.vue";
import SearchInput from "@/Components/Reels/SearchInput.vue";
import {getFathomPagePrefix, trackEvent, trackFathomEvent} from "@/Helpers/analytics";
import {getCreateCastingUrl} from "@/Helpers/auditions";

const props = defineProps({
    view: {
        type: String,
        required: true,
        validator: (value) => ['all', 'explore', 'mine', 'released', 'sponsored', 'castings'].includes(value)
    },
    isUserLoggedIn: {
        type: Boolean,
        required: true
    },
    sponsoredThisMonth: {
        type: Number,
        required: true
    },
    sponsoredLast30Days: {
        type: Number,
        required: true
    },
    totalSponsored: {
        type: Number,
        required: true
    },
    sponsoredTotalStarsGiven: {
        type: Number,
        required: true
    },
    totalStarsGiven: {
        type: Number,
        required: true
    },
    userSponsoredCount: {
        type: Number,
        required: true
    },
    releasedCount: {
        type: Number,
        required: true
    },
    userTotalStarsGiven: {
        type: Number,
        required: true
    },
    topSponsors: {
        type: Array,
        default: []
    },
    searchQuery: {
        type: String,
        default: ''
    },
    routePrefix: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['openLeaderboard', 'joinClick', 'myNaClicked', 'searchInput', 'clearSearch', 'searchFocus', 'castingClick', 'castingsPageClick'])

const heroRef = ref(null)
const maxVisibleTopSponsors = ref(3)

const heroTitle = computed(() => {
    if (props.view === 'mine') return 'My Sponsorships'
    if (props.view === 'released') return 'Sponsored & Released'
    if (props.view === 'sponsored') return 'Community-Backed Auditions'
    if (props.view === 'castings') return 'Pitch Requests'
    return 'Sponsor Auditions. Build Your Legacy.'
})

const rankBadgeColor = (rank) => {
    if (rank === 1) return 'from-amber-300 to-amber-500';
    if (rank === 2) return 'from-zinc-300 to-zinc-500';
    return 'from-amber-600 to-amber-800'
}

const rankTextColor = (rank) => {
    if (rank === 1) return 'text-amber-300/80';
    if (rank === 2) return 'text-white/50';
    return 'text-white/45'
}

const formatStars = (num) => {
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M+`;
    }
    if (num >= 100000) {
        return `${Math.floor(num / 1000)}K+`;
    }
    return num.toLocaleString()
}

const goToSponsorProfile = (sponsor) => {
    trackEvent('MyNA Clicked')
    trackFathomEvent('MyNA Clicked')

    router.visit(route(props.routePrefix + '.profile.my', { slug: sponsor.sponsor_name }), {
        preserveScroll: false,
        preserveState: true,
    })
}

const handleCreateCastingClick = () => {
    trackFathomEvent(getFathomPagePrefix(props.routePrefix) + '_Hero_Create_Casting_Button_Clicked')
}

const handleCastingsPageClick = () => {
    trackFathomEvent(getFathomPagePrefix(props.routePrefix) + '_Hero_Castings_Page_Clicked')
    emit('castingsPageClick')
}

</script>
